/**
 * PDF Link Handler with Mobile/Tablet Optimization
 * Global solution for opening PDFs in new tabs instead of downloading
 */

/**
 * Initialize PDF handler for all PDF links
 */
export function initPdfHandler() {
    // More aggressive PDF link detection
    const pdfLinks = document.querySelectorAll('.pdf-link, a[href$=".pdf"], a[href*=".pdf"]');
    
    if (pdfLinks.length === 0) {
        // console.log('PDF Handler: No PDF links found');
        return;
    }
    
    console.log(`PDF Handler: Found ${pdfLinks.length} PDF links`);
    console.log('PDF Handler: User Agent:', navigator.userAgent);
    
    pdfLinks.forEach((link, index) => {
        console.log(`PDF Handler: Processing link ${index + 1}:`, link.href);
        
        // Remove download attribute if present
        if (link.hasAttribute('download')) {
            console.log(`PDF Handler: Removing download attribute from link ${index + 1}`);
            link.removeAttribute('download');
        }
        
        // Add click handler for enhanced mobile experience
        link.addEventListener('click', handlePdfClick);
        
        // Add loading indicator
        link.addEventListener('click', showPdfLoading);
        
        // Add PDF icon if not present
        addPdfIcon(link);
        
        // Add visual indicator that this link has been processed
        link.setAttribute('data-pdf-handler', 'active');
    });
}

/**
 * Handle PDF link clicks with device-specific optimizations
 */
function handlePdfClick(event) {
    const link = event.currentTarget;
    const href = link.getAttribute('href');
    
    if (!href || !href.toLowerCase().includes('.pdf')) {
        return true; // Not a PDF link, proceed normally
    }
    
    // Device detection
    const isMobile = /Mobile|Android|iPhone|iPod/i.test(navigator.userAgent);
    const isTablet = /iPad|Tablet/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    console.log('PDF Handler: Device detection', {
        isMobile,
        isTablet,
        isIOS,
        isAndroid,
        userAgent: navigator.userAgent
    });
    
    // iOS Safari specific handling
    if (isIOS) {
        // iOS Safari handles PDFs well in new tabs
        console.log('PDF Handler: iOS device detected, using default behavior');
        return true; // Let default behavior proceed
    }
    
    // Android mobile specific handling
    if (isMobile && isAndroid) {
        console.log('PDF Handler: Android mobile detected, implementing fallback strategy');
        
        // For Android, immediately show the fallback modal instead of trying direct link
        // This gives users better control over how they want to view the PDF
        event.preventDefault();
        showPdfFallbackModal(href);
        return false;
    }
    
    // Desktop and tablet default behavior
    console.log('PDF Handler: Desktop/tablet detected, using default behavior');
    return true;
}

/**
 * Show loading indicator while PDF loads
 */
function showPdfLoading(event) {
    const link = event.currentTarget;
    
    // Add loading state
    link.classList.add('pdf-loading');
    
    // Remove loading state after delay
    setTimeout(() => {
        link.classList.remove('pdf-loading');
    }, 2000);
}

/**
 * Show fallback modal for problematic PDF viewing
 */
function showPdfFallbackModal(originalUrl) {
    // Create Google Docs viewer URL as fallback
    const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(originalUrl)}&embedded=true`;
    
    // Create modal element
    const modal = document.createElement('div');
    modal.className = 'pdf-fallback-modal';
    modal.innerHTML = `
        <div class="modal-content">
            <h3>View PDF Document</h3>
            <p>Choose how you'd like to view this PDF on your device:</p>
            <div class="pdf-options">
                <a href="${googleDocsUrl}" target="_blank" class="btn btn-primary">
                    <span class="icon">👁️</span>
                    View in Browser
                </a>
                <a href="${originalUrl}" target="_blank" class="btn btn-secondary">
                    <span class="icon">📄</span>
                    Open PDF Directly
                </a>
                <a href="${originalUrl}" download class="btn btn-outline">
                    <span class="icon">⬇️</span>
                    Download PDF
                </a>
            </div>
            <button class="close-modal" aria-label="Close modal">&times;</button>
        </div>
    `;
    
    // Add modal to page
    document.body.appendChild(modal);
    
    // Focus management for accessibility
    const firstButton = modal.querySelector('.btn');
    if (firstButton) {
        firstButton.focus();
    }
    
    // Close modal functionality
    const closeButton = modal.querySelector('.close-modal');
    closeButton.addEventListener('click', () => {
        closeModal(modal);
    });
    
    // Close on backdrop click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeModal(modal);
        }
    });
    
    // Close on escape key
    const handleEscape = (e) => {
        if (e.key === 'Escape') {
            closeModal(modal);
            document.removeEventListener('keydown', handleEscape);
        }
    };
    document.addEventListener('keydown', handleEscape);
    
    console.log('PDF Handler: Fallback modal displayed');
}

/**
 * Close modal and clean up
 */
function closeModal(modal) {
    if (modal && modal.parentNode) {
        document.body.removeChild(modal);
    }
}

/**
 * Add PDF icon to links that don't have one
 */
function addPdfIcon(link) {
    // Check if link already has an icon
    const hasIcon = link.querySelector('.pdf-icon, .icon');
    
    if (!hasIcon) {
        const icon = document.createElement('span');
        icon.className = 'pdf-icon';
        icon.innerHTML = '📄';
        icon.style.marginRight = '0.5rem';
        
        // Insert icon at the beginning of the link
        link.insertBefore(icon, link.firstChild);
    }
}

/**
 * Utility function to check if URL is a PDF
 */
export function isPdfUrl(url) {
    if (!url) return false;
    return url.toLowerCase().includes('.pdf');
}

/**
 * Utility function to get PDF file size (if available)
 */
export async function getPdfFileSize(url) {
    try {
        const response = await fetch(url, { method: 'HEAD' });
        const contentLength = response.headers.get('content-length');
        
        if (contentLength) {
            const bytes = parseInt(contentLength, 10);
            return formatFileSize(bytes);
        }
    } catch (error) {
        console.log('PDF Handler: Could not get file size', error);
    }
    
    return null;
}

/**
 * Format file size in human readable format
 */
function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}